import axios from 'axios'
import Vue from 'vue'

import env from './ActionForm/utils/env'
import { keyMirror } from './ActionForm/utils/data'
import { getCountryPermittedFields, isEu, isLgpd, akCountries } from './ActionForm/utils/countries'
import { EVENT_CATEGORIES, EVENT_ACTIONS } from './ActionForm/utils/tracking'

import SelectInput from './ActionForm/components/fields/SelectInput'
import TextInput from './ActionForm/components/fields/TextInput'

import Vuetify from '~/plugins/vuetify'
import { CardPlugin } from 'bootstrap-vue'

export const FORM_STATES = keyMirror(
  'INITIAL',
  'THANKS',
  'DEACTIVATED',
  'LTT_CHOOSE_REP',
  'LTT_EMAIL',
  'FUNDRAISER_INITIAL',
  'FUNDRAISER_PERSONAL',
  'FUNDRAISER_PAYMENT',
  'FUNDRAISER_THANKS'
)

export const LTT_SINGLE_REP_PANEL_TABS = [FORM_STATES.LTT_EMAIL, FORM_STATES.THANKS]
export const LTT_PANEL_TABS = [FORM_STATES.LTT_CHOOSE_REP, ...LTT_SINGLE_REP_PANEL_TABS]

// initial state
const state = () => ({
  formState: FORM_STATES.INITIAL,
  formConfig: null,
  fieldDefinitions: [],
  formData: {},
  formGlobalErrors: [],
  formIsDirty: false,
  repId: null,
  repEmailData: null,
  postData: null,
  lttPostalCode: null,
  baseUrl: null,
  actionId: null,
  fetching: false,
  forcePostalCodeError: false,
  lttConstituencies: [],
  locale: null,
  has3pEmailConsent: false,
  getClientRect: null,
  $gtm: null,
  $gtag: null,
  initialised: false,
  showOneClick: false,
  countryWarningEnabled: true,
  lttConverted: false,
  urlQuery: {},
  akUserData: null,
  stats: {},
  fundraiserUI: {
    currency: 'USD',
    amount: '',
    overrides: {},
    selectedPaymentFrequency: 'one_time',
    selectedProviderMethod: 'stripe_payment',
    selectedProvider: 'stripe',
    amountSelectionIsDirty: false,
    stripeReady: false,
    stripeClientSecret: null,
    transation_id: null
  },
  socket: {
    isConnected: false,
    messages: [],
    reconnectError: false
  },
  socketIsConnected: false,
  petitions: [],
  petitionsSearchResults: []
})

const mutations = {
  SOCKET_ONOPEN(state, event) {
    Vue.prototype.$socket = event.currentTarget
    state.socket.isConnected = true
    state.socketIsConnected = true
  },
  SOCKET_ONCLOSE(state, event) {
    state.socketIsConnected = false
    state.socket.isConnected = true
  },
  SOCKET_ONERROR(state, event) {
    // eslint-disable-next-line no-console
    console.error(state, event)
  },
  // default handler called for all methods
  SOCKET_ONMESSAGE(state, message) {
    state.socket.messages.push(message)
  },
  // mutations for reconnect methods
  SOCKET_RECONNECT(state, count) {},
  SOCKET_RECONNECT_ERROR(state) {
    state.socket.reconnectError = true
  },
  SET_FORM_STATE(state, formState) {
    state.formState = formState
  },
  SET_FORM_CONFIG(state, formConfig) {
    state.formConfig = formConfig
  },
  SET_PRESELECTED_ONEOFF_AMOUNT(state, amount) {
    state.formConfig.preselected_one_time_amount = amount
  },
  SET_PRESELECTED_MONTHLY_AMOUNT(state, amount) {
    state.formConfig.preselected_monthly_amount = amount
  },
  SET_REP_ID(state, repId) {
    state.repId = repId
  },
  SET_REP_EMAIL_DATA(state, repEmailData) {
    state.repEmailData = repEmailData
  },
  SET_POST_DATA(state, postData) {
    state.postData = postData
  },
  UPDATE_POST_DATA(state, data) {
    state.postData = { ...data, ...state.postData }
  },
  SET_LTT_POSTAL_CODE(state, postalCode) {
    state.lttPostalCode = postalCode
  },
  SET_BASE_URL(state, baseUrl) {
    state.baseUrl = baseUrl
  },
  SET_ACTION_ID(state, actionId) {
    state.actionId = actionId
  },
  SET_FETCHING(state, fetching) {
    state.fetching = fetching
  },
  SET_FORCE_POSTAL_CODE_ERROR(state, forcePostalCodeError) {
    state.forcePostalCodeError = forcePostalCodeError
  },
  SET_LTT_CONSTITUENCY_DATA(state, lttConstituencies) {
    state.lttConstituencies = lttConstituencies
  },
  SET_LOCALE(state, locale) {
    state.locale = locale
  },
  SET_HAS_3P_EMAIL_CONSENT(state, has3pEmailConsent) {
    state.has3pEmailConsent = has3pEmailConsent
  },
  SET_GET_CLIENT_RECT(state, getClientRect) {
    state.getClientRect = getClientRect
  },
  UPDATE_FORM_DATA(state, data) {
    state.formData = { ...state.formData, ...data }
  },
  UPDATE_FORM_DATA_FIELD_VALUES(state, { fieldConfig, value }) {
    const newData = {}
    newData[fieldConfig.name] = value
    state.formData = { ...state.formData, ...newData }
    state.formIsDirty = true
  },
  SET_SMS_OPT_IN(state, value) {
    state.formData.user_sms_opt_in = value
  },
  SET_FIELD_DEFINITIONS(state, fields) {
    state.fieldDefinitions = fields
  },
  SET_DOLLAR_GTAG(state, $gtag) {
    state.$gtag = $gtag
  },
  SET_DOLLAR_GTM(state, $gtm) {
    state.$gtm = $gtm
  },
  SET_AK_USER_DATA(state, data) {
    state.akUserData = data
  },
  SET_INITIALISED_TRUE(state) {
    state.initialised = true
  },
  SET_SHOW_ONECLICK(state, show) {
    state.showOneClick = show
  },
  SET_COUNTRY_WARNING_ENABLED(state, enabled) {
    state.countryWarningEnabled = enabled
  },
  SET_LTT_CONVERTED(state, converted) {
    state.lttConverted = converted
  },
  SET_URL_QUERY(state, urlQuery) {
    state.urlQuery = urlQuery
  },
  SET_FORM_STATS(state, stats) {
    state.stats = stats
  },
  UPDATE_FUNDRAISER_UI(state, newData) {
    state.fundraiserUI = { ...state.fundraiserUI, ...newData }
  },
  APPEND_FORM_GLOBAL_ERRORS(state, error) {
    state.formGlobalErrors.push(error)
  },
  CLEAR_FORM_GLOBAL_ERRORS(state) {
    state.formGlobalErrors = []
  },
  SET_ACTION_SOCKET(state, actionSocket) {
    state.actionSocket = actionSocket
  },
  SET_POPULAR_PETITION_DATA(state, popularPetitions) {
    state.popularPetitions = popularPetitions
  },
  SET_PETITION_DATA(state, petitions) {
    state.petitions = petitions
  },
  SET_PETITION_PAGE(state, page) {
    state.page = page
  },
  SET_PETITION_ENDPOINT(state, endpoint) {
    state.endpoint = endpoint
  },
  SET_PETITION_SEARCH_TERM(state, searchTerm) {
    state.searchTerm = searchTerm
  },
  SET_PETITION_SEARCH_RESULTS(state, petitionsSearchResults) {
    state.petitionsSearchResults = petitionsSearchResults
  }
}

const resolveExtraForField = ({ state, getters }, { name }) => {
  switch (name) {
    case 'country':
      return {
        component: SelectInput,
        options: state.formConfig.countries,
        required: true
      }
    case 'first_name':
    case 'last_name':
      return {
        component: TextInput,
        maxlength: 50,
        required: getters.isFundraiser || getters.isLTT
      }
    case 'email':
      return {
        type: 'email',
        component: TextInput,
        maxlength: 100,
        required: true
      }
    case 'address':
      return {
        component: TextInput,
        maxlength: 255
      }
    case 'city':
      return {
        component: TextInput,
        maxlength: 100
      }
    case 'region':
      return {
        component: TextInput,
        maxlength: 100
      }
    case 'state':
      return {
        component: SelectInput,
        emptyOption: {
          $t: 'action.fields.state.label'
        },
        options: state.formConfig.us_states
      }
    case 'postal_code':
      return {
        type: getters.signerCountryHasNumericPostcode ? 'number' : 'text',
        component: TextInput,
        maxlength: 20,
        // Postal code is required for Letter to Target campaigns
        required: state.formConfig.type === 'letter_to_target'
      }
    case 'phone':
      return {
        type: 'tel',
        component: TextInput,
        maxlength: 20
      }
    default:
      throw new Error(`Unknown field name "${name}"`)
  }
}

const getAmountsWithInsertedValue = (amounts, value) => {
  // Attempts to insert a new value to the amounts array and drops one of the older amounts
  if (!isNaN(value)) {
    // Number is good, add it to the array if it isn't already
    if (!amounts.includes(value)) {
      amounts.push(value)
      // Sort the values in descending order
      amounts.sort((a, b) => b - a)
      // Drop a value out of the array that is not the newly added value
      if (value === amounts[amounts.length - 1]) {
        // If the value is at the end, drop the second last value
        amounts = amounts.slice(0, -2).concat(amounts[amounts.length - 1])
      } else {
        // Otherwise drop the last item (the lowest)
        amounts = amounts.slice(0, -1)
      }
    }
  }
  return amounts
}

const getters = {
  countriesRestricted: state => (state.formConfig && state.formConfig.countries_restricted) || [],
  isPetition: state =>
    state.formConfig &&
    (state.formConfig.type === 'petition' || state.formConfig.type === 'member_petition'),
  isLTT: state => state.formConfig && state.formConfig.type === 'letter_to_target',
  isSingleRepLTT: (state, getters) =>
    getters.isLTT && state.formConfig && state.formConfig.ltt_single_rep_id !== null,
  isFundraiser: state => state.formConfig && state.formConfig.is_fundraiser,
  isMemberFundraiser: state => state.formConfig && state.formConfig.type === 'member_fundraiser',
  goalShowsAmountRaised: (state, getters) =>
    getters.isFundraiser && state.formConfig.goal_shows_amount_raised,
  panelTabs: (state, getters) =>
    getters.isSingleRepLTT ? LTT_SINGLE_REP_PANEL_TABS : getters.isLTT ? LTT_PANEL_TABS : [],
  showPanelTabs: (state, getters) => getters.panelTabs.includes(state.formState),
  panelTabEnabled: state => panelTab => {
    switch (panelTab) {
      case FORM_STATES.LTT_CHOOSE_REP:
        return true
      case FORM_STATES.LTT_EMAIL:
        return Boolean(state.repId)
      case FORM_STATES.THANKS:
        return Boolean(state.repEmailData)
    }
    return false
  },
  panelTabIndex: (state, getters) => getters.panelTabs.indexOf(state.formState),
  signerCountryHasNumericPostcode: (state, getters) =>
    // eslint-disable-next-line unicorn/prefer-includes
    ['GB'].indexOf(state.formData.country) < 0,
  stateFieldPermitted: state => state.formData && state.formData.country === 'US',
  isInteractive: state => state.formConfig.is_interactive,
  fieldConfigs: (state, getters) => {
    return getCountryPermittedFields(state.fieldDefinitions, state.formData.country)
      .map(([name, label]) => ({
        name,
        label,
        disabled: !getters.isInteractive,
        ...resolveExtraForField({ state, getters }, { name })
      }))
      .filter(function ({ name }) {
        // Keep the rest
        switch (name) {
          // Drop phone field if phoneFieldPermitted computed property is false
          case 'phone':
            return getters.phoneFieldPermitted
          // Drop state field if stateFieldPermitted computed property is false
          case 'state':
            return getters.stateFieldPermitted
          // Drop region field if country is US and state field enabled
          case 'region':
            return !(getters.stateFieldPermitted && state.formData.country === 'US')
          // Drop postal code field if it's already on the form via the Stripe element
          case 'postal_code':
            return !(
              getters.isFundraiser &&
              state.fundraiserUI.selectedProvider === 'stripe' &&
              state.fundraiserUI.selectedProviderMethod === 'stripe_payment'
            )
        }
        return true
      })
  },
  isGdprCountry: state => isEu(state.formData.country),
  isLgpdCountry: state => isLgpd(state.formData.country),
  akName: state => state.akUserData && state.akUserData.name,
  optOutChosen: state => state.formData.user_gdpr_opt && state.formData.user_gdpr_opt === 'NO',
  hasPhoneField: (state, getters) =>
    Boolean(getters.fieldConfigs.find(field => field.name === 'phone')),
  isMemberGenerated: state => state.formConfig.is_member_generated,
  showOptIn: (state, getters) =>
    !state.showOneClick &&
    !getters.showLttCountryWarning &&
    ((state.formConfig && state.formConfig.opt_in_mode === 'all') ||
      (state.formConfig &&
        state.formConfig.opt_in_mode === 'gdpr' &&
        (getters.isGdprCountry || getters.isLgpdCountry))),
  showSmsOptIn: (state, getters) =>
    getters.phoneFieldPermitted && getters.hasPhoneField && !getters.showOneClick,
  showSpinnerOverlay: state => !state.initialised || state.fetching,
  showLttCountryWarning: (state, getters) =>
    getters.isLTT &&
    state.countryWarningEnabled &&
    getters.countriesRestricted.length > 0 &&
    // eslint-disable-next-line unicorn/prefer-includes
    getters.countriesRestricted.map(([code]) => code).indexOf(state.formConfig.country_code) < 0,
  enabledCurrencies: state =>
    state.formConfig.enabled_currencies.filter(
      // eslint-disable-next-line camelcase
      ({ country_codes_disabled }) =>
        !country_codes_disabled.includes(state.formConfig.country_code)
    ),
  enabledCurrencyCodes: (state, getters) => getters.enabledCurrencies.map(({ code }) => code),
  currencyConfigLookup: state =>
    state.formConfig.enabled_currencies.reduce((acc, currencyConfig) => {
      acc[currencyConfig.code] = currencyConfig
      return acc
    }, {}),
  currentCurrencyConfig: (state, getters) =>
    getters.currencyConfigLookup[state.fundraiserUI.currency] || {},
  oneTimePreselectedAmount: (state, getters) =>
    state.formConfig.preselected_one_time_amount ||
    getters.currentCurrencyConfig.preselected_one_time_amount,
  oneTimeDonationAmounts: (state, getters) => {
    const amounts =
      (state.formConfig.one_time_payment_options.length &&
        state.formConfig.one_time_payment_options.concat()) ||
      (getters.currentCurrencyConfig.one_time_payment_options.length &&
        getters.currentCurrencyConfig.one_time_payment_options.concat())
    // Sort the values in descending order
    amounts.sort((a, b) => b - a)
    return amounts
  },
  oneTimeDonationAmountsWithInserted: (state, getters) => {
    return getAmountsWithInsertedValue(
      getters.oneTimeDonationAmounts,
      state.fundraiserUI.overrides.one_time || getters.oneTimePreselectedAmount
    )
  },
  monthlyPreselectedAmount: (state, getters) =>
    state.formConfig.preselected_monthly_amount ||
    getters.currentCurrencyConfig.preselected_monthly_amount,
  monthlyDonationAmounts: (state, getters) => {
    const amounts =
      (state.formConfig.monthly_payment_options.length &&
        state.formConfig.monthly_payment_options.concat()) ||
      (getters.currentCurrencyConfig.monthly_payment_options.length &&
        getters.currentCurrencyConfig.monthly_payment_options.concat())
    // Sort the values in descending order
    amounts.sort((a, b) => b - a)
    return amounts
  },
  monthlyDonationAmountsWithInserted: (state, getters) => {
    return getAmountsWithInsertedValue(
      getters.monthlyDonationAmounts,
      state.fundraiserUI.overrides.monthly || getters.monthlyPreselectedAmount
    )
  },
  donationAmounts: (state, getters) => {
    switch (state.fundraiserUI.selectedPaymentFrequency) {
      case 'one_time':
        return getters.oneTimeDonationAmounts
      case 'monthly':
        return getters.monthlyDonationAmounts
      default:
        throw new Error(
          `Unhandled selectedPaymentFrequency ${state.fundraiserUI.selectedPaymentFrequency}`
        )
    }
  },
  donationAmountsWithInserted: (state, getters) => {
    switch (state.fundraiserUI.selectedPaymentFrequency) {
      case 'one_time':
        return getters.oneTimeDonationAmountsWithInserted
      case 'monthly':
        return getters.monthlyDonationAmountsWithInserted
      default:
        throw new Error(
          `Unhandled selectedPaymentFrequency ${state.fundraiserUI.selectedPaymentFrequency}`
        )
    }
  },
  currencySelectOptGroups: (state, getters) => {
    // Create first opt group of currency options, listing the most common currencies, as long as
    // they're not blacklisted by the current country choice
    const topCodes = ['USD', 'EUR', 'GBP'].filter(
      code =>
        getters.currencyConfigLookup[code] &&
        !getters.currencyConfigLookup[code].country_codes_disabled.includes(
          state.formConfig.country_code
        )
    )
    // Append the default currency for the detected country as long as it too is not blacklisted
    if (
      !(
        topCodes.includes(state.formConfig.country_default_currency) ||
        getters.currencyConfigLookup[
          state.formConfig.country_default_currency
        ].country_codes_disabled.includes(state.formConfig.country_code)
      )
    ) {
      topCodes.unshift(state.formConfig.country_default_currency)
    }

    // Create the second opt group of enabled currency options, excluding those already listed
    // in the first group
    const remainingCodes = getters.enabledCurrencyCodes.filter(code => !topCodes.includes(code))
    // Generate an array of the 2 groups
    const optGroups = [
      topCodes.map(code => [code, `${code} - ${getters.currencyConfigLookup[code].name}`]),
      remainingCodes.map(code => [code, `${code} - ${getters.currencyConfigLookup[code].name}`])
    ]
    // Return all optGroups that contain items
    return optGroups.filter(optGroup => optGroup.length)
  },
  minimumDonationAmount: (state, getters) => getters.currentCurrencyConfig.minimum_donation_amount,
  paypalEnabled: state =>
    state.formConfig.enabled_providers.map(provider => provider.name).includes('paypal') &&
    state.formConfig.paypal_currencies.includes(state.fundraiserUI.currency),
  // Return the full name as entered on the form.
  fullName: state => {
    return ['first_name', 'last_name'].reduce((acc, field) => {
      if (state.formData[field]) {
        return acc ? acc.concat(` ${state.formData[field]}`) : state.formData[field]
      }
      return acc
    }, '')
  },
  withConfetti: state =>
    (state.fundraiserUI.selectedPaymentFrequency === 'monthly' &&
      state.formConfig.monthly_donation_confetti) ||
    (state.fundraiserUI.selectedPaymentFrequency === 'one_time' &&
      state.formConfig.one_time_donation_confetti)
}

const generateActionData = (state, getters) => {
  // Generates ActionKit action data
  let actionData = {
    page: state.formConfig.ak_name
  }

  if (state.has3pEmailConsent) {
    actionData.action_3p_email_optin = 'YES'
  }

  if (getters.showOptIn) {
    actionData.user_gdpr_timestamp = Math.round(new Date().getTime() / 1000)

    if (getters.optOutChosen) {
      actionData.unsub_all = 1
    }

    actionData.user_gdpr_opt = state.formData.user_gdpr_opt
  }

  if (state.showOneClick) {
    if (!actionData.unsub_all) {
      // suppress_subscribe neither subscribes or unsubscribes the user. If we are just signing
      // with the recognised akid we obviously don't want to change the user's current
      // subscription status
      actionData.suppress_subscribe = 1
    }
  } else {
    actionData = { ...actionData, ...state.formData }
    if (actionData.country) {
      // Translate the standard country code to the ActionKit equivalent value
      actionData.country = akCountries[actionData.country].value
    }
    if (actionData.address) {
      // It's actually stored as address1 in ActionKit
      actionData.address1 = actionData.address
      delete actionData.address
    }
    if (actionData.postal_code) {
      // It's actually stored as postal in ActionKit
      actionData.postal = actionData.postal_code
      delete actionData.postal_code
    }
    if (actionData.region && actionData.state) {
      // If country is US, send state, not region and vice versa
      if (actionData.country === akCountries['US'].value) {
        delete actionData.region
      } else {
        delete actionData.state
      }
    }

    // Remove any fields submitted with null values as long as they're not required
    actionData = Object.keys(actionData).reduce((acc, key) => {
      if (
        actionData[key] !== null ||
        (actionData[key] === null &&
          getters.fieldConfigs[key] &&
          getters.fieldConfigs[key].required)
      ) {
        acc[key] = actionData[key]
      }
      return acc
    }, {})
  }

  if (state.urlQuery.akid && getters.isPetition) {
    actionData.akid = state.urlQuery.akid
    // If we are sending an akid, we do not send the email
    // AKIDs link the action to the email mailing
    delete actionData.email
  }

  // Append google analytics query parameters as custom action fields values to actionData
  // We persist the google analytics query parameters to ActionKit this way
  return {
    ...actionData,
    ...['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].reduce(
      (acc, key) => {
        if (Object.prototype.hasOwnProperty.call(state.urlQuery, key)) {
          acc[`action_${key}`] = state.urlQuery[key]
        }
        return acc
      },
      {}
    )
  }
}

const actions = {
  async fetchFormConfig({ commit, state, getters }) {
    try {
      const response = await axios.get(`${state.baseUrl}/api/campaigns/actions/${state.actionId}/`)
      const {
        locale,
        config,
        // eslint-disable-next-line camelcase
        enabled_currencies,
        // eslint-disable-next-line camelcase
        enabled_providers,
        // eslint-disable-next-line camelcase
        one_time_payment_options,
        // eslint-disable-next-line camelcase
        preselected_one_time_amount,
        // eslint-disable-next-line camelcase
        monthly_payment_options,
        // eslint-disable-next-line camelcase
        preselected_monthly_amount,
        // eslint-disable-next-line camelcase
        zero_decimal_currencies,
        // eslint-disable-next-line camelcase
        currency_rate,
        stats,
        fields
      } = response.data
      const formConfig = {
        enabled_currencies,
        enabled_providers,
        one_time_payment_options,
        preselected_one_time_amount,
        monthly_payment_options,
        preselected_monthly_amount,
        zero_decimal_currencies,
        currency_rate,
        ...config
      }

      commit('SET_LOCALE', locale)
      commit('SET_FORM_CONFIG', formConfig)
      commit('SET_FORM_STATS', stats)
      commit('SET_FIELD_DEFINITIONS', fields)

      let query = {}
      if (this.$router) {
        // SSR / Nuxt way
        query = this.$router.currentRoute.query
      } else if (window) {
        // Client side / Non-nuxt way
        const url = new URL(window.location.href)
        url.searchParams.forEach((value, key) => (query[key] = value))
      }
      commit('SET_URL_QUERY', query)

      if (response.data.config.status === 'deactivated') {
        commit('SET_FORM_STATE', FORM_STATES.DEACTIVATED)
      } else {
        commit('UPDATE_FORM_DATA', {
          user_gdpr_opt: null,
          city: '-',
          ...fields.reduce((acc, [name]) => {
            if (name === 'country') {
              // initialise country dropdown selection
              acc[name] = formConfig.country_code
            } else {
              acc[name] = null
            }
            return acc
          }, {})
        })
        if (getters.isFundraiser) {
          // Fundraiser mode initialisation
          // eslint-disable-next-line camelcase
          const { one_time, monthly, show_monthly, preselected } = query
          const overrides = { one_time, monthly }
          const defaultCurrency = formConfig.country_default_currency || 'USD'

          commit('UPDATE_FUNDRAISER_UI', {
            overrides,
            // eslint-disable-next-line camelcase
            selectedPaymentFrequency: show_monthly ? 'monthly' : 'one_time',
            // Calculate the default permitted currency (takes into account country blacklists etc)
            currency:
              getters.currencyConfigLookup[defaultCurrency] &&
              getters.currencyConfigLookup[defaultCurrency].country_codes_disabled.includes(
                formConfig.country_code
              )
                ? getters.enabledCurrencyCodes[0]
                : defaultCurrency
          })
          // If preselected is set to 0, this suppresses the amount preselection on the paymeent
          // selection panel. Otherwise we preselect the appropriate amount
          // Do not merge the below line with earlier UPDATE_FUNDRAISER_UI commits.
          // The getter relies on overrides already being set in state
          if (preselected !== '0') {
            commit('UPDATE_FUNDRAISER_UI', {
              // eslint-disable-next-line camelcase
              amount: show_monthly
                ? getters.monthlyPreselectedAmount
                : getters.oneTimePreselectedAmount
            })
          }
          commit('SET_FORM_STATE', FORM_STATES.FUNDRAISER_INITIAL)
        }
      }

      return formConfig
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
    return null
  },
  async fetchUserData({ commit, state, getters }) {
    // Only run this action after fetchFormConfig has been completed
    if (state.urlQuery.akid && state.formConfig && state.formConfig.status !== 'deactivated') {
      try {
        // Attempt to fetch the publicly available user data from ActionKit for this AKID
        const response = await axios.get(
          `${env.AK_REST_ENDPOINT}/userpublic/${state.urlQuery.akid}/`
        )
        // If we get it, save it in the store. This provides the values for the akid and akName
        // getters, amongst other things
        commit('SET_AK_USER_DATA', response.data)
        // Conditionally show the one click signing option
        commit(
          'SET_SHOW_ONECLICK',
          // ak_proof is present in the url query and set to '1' if the page is linked from a
          // proof email test. We do not initiate a one-click in this circumstance
          state.urlQuery.ak_proof !== '1' &&
            // We also only provide one-click for petitions
            getters.isPetition
        )
      } catch (e) {
        const { status } = e.response || {}
        if (status !== 404) {
          // eslint-disable-next-line no-console
          console.error(e)
        }
      }
    }
    commit('SET_INITIALISED_TRUE')
  },
  handleFormSubmit({ state, getters, commit, dispatch }, { executeRecaptcha }) {
    // Don't process anything if the form should not be enabled
    if (!getters.isInteractive) {
      return
    }
    commit('SET_FETCHING', true)

    if (state.formConfig.recaptcha_enabled) {
      // if recaptcha is enabled request the verification code from google
      executeRecaptcha()
    } else {
      // No verification required, just submit to ActionKit
      dispatch('postToAK')
    }
  },
  handleConversionTracking({ state, commit, getters, dispatch }) {
    const { $gtag, $gtm } = state

    try {
      // Handle conversion based on the type of form

      if (getters.isFundraiser) {
        // Handle fundraiser conversions

        // Fundraiser Facebook conversion tracking
        if (window.fbq) {
          window.fbq('track', 'Purchase', {
            value: state.fundraiserUI.amount,
            currency: state.fundraiserUI.currency
          })
        }
        // Fundraiser Twitter conversion tracking
        if (window.twq) {
          window.twq('track', 'Purchase', {
            value: state.fundraiserUI.amount,
            currency: state.fundraiserUI.currency,
            num_items: 1
          })
        }
      } else if (getters.isLTT) {
        // Handle LTT conversions

        // Ltt actions can have multiple actions in a single conversion, so we make sure we only
        // indicate a conversion once
        if (!state.lttConverted) {
          // LTT Facebook conversion tracking
          if (window.fbq) {
            window.fbq('trackCustom', 'lttEmailSent', {})
          }
          // LTT Twitter conversion tracking
          if (window.twq) {
            window.twq('track', 'Custom')
          }
          commit('SET_LTT_CONVERTED', true)
        }
      } else if (getters.isPetition) {
        // Handle petition conversions

        // Petition Facebook conversion tracking
        if (window.fbq) {
          // Fire Facebook Pixel "Lead" event to signify a conversion on a petition page
          window.fbq('track', 'Lead')
        }
        // Petition Twitter conversion tracking
        if (window.twq) {
          // Fire Twitter Pixel "Sign Up" event to signify a conversion on a petition page
          window.twq('track', 'Sign Up')
        }
      }

      // Handle conversion events common to all form types

      // Common Facebook conversion tracking
      if (window.fbq) {
        // Fire Subscribe event if this submission is an opt-in to the member list
        if (!(getters.showOptIn && getters.optOutChosen)) {
          window.fbq('track', 'Subscribe')
        }
      }

      // Common Twitter conversion tracking
      if (window.twq) {
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e)
    }
  },
  sendSocketData(context, data) {
    Vue.prototype.$socket.send(JSON.stringify(data))
  },
  incrementActionCount({ dispatch, getters, state }) {
    if (getters.isFundraiser) {
      const { country } = state.formData
      const { currency_rate: currencyRate } = state.formConfig
      const { currency, amount } = state.fundraiserUI

      // Convert amount to base currency
      const baseAmount =
        currency !== currencyRate.base_currency && currencyRate.rates[currency]
          ? amount / currencyRate.rates[currency]
          : amount

      const donationInfo = { country, currency, baseAmount, amount }
      dispatch('sendSocketData', { donation_inc: donationInfo })
    } else {
      dispatch('sendSocketData', { sig_inc: true })
    }
  },
  // Implements the signature post to ActionKit
  // #TODO: Implement in django async
  async postToAK({ state, dispatch, commit, getters }) {
    commit('SET_POST_DATA', generateActionData(state, getters))
    commit('SET_FETCHING', true)

    try {
      if (!state.urlQuery.akid && state.showOneClick) {
        throw new Error('One-click requires akid')
      }

      await axios({
        method: 'post',
        url: `${env.AK_REST_ENDPOINT}/action/`,
        headers: {},
        data: state.postData
      })

      // Switch to the next panel
      dispatch('changeToNextPanel')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
      throw e
    } finally {
      commit('SET_FETCHING', false)
    }

    dispatch('scrollToTopIfAboveTheFold')
  },
  handleRepChosen({ dispatch }, repId) {
    dispatch('fetchLttEmailInfo', { repId })
    dispatch('changeToNextPanel')
  },
  changeToNextPanel({ commit, state, dispatch, getters }) {
    if (
      // eslint-disable-next-line unicorn/prefer-includes
      [
        FORM_STATES.INITIAL,
        FORM_STATES.LTT_CHOOSE_REP,
        FORM_STATES.LTT_EMAIL,
        FORM_STATES.FUNDRAISER_INITIAL,
        FORM_STATES.FUNDRAISER_PERSONAL,
        FORM_STATES.FUNDRAISER_PAYMENT
      ].indexOf(state.formState) >= 0
    ) {
      dispatch('scrollToTopIfAboveTheFold')
    }
    switch (state.formState) {
      case FORM_STATES.INITIAL:
        if (getters.isSingleRepLTT) {
          return dispatch('fetchLttEmailInfo', {
            repId: state.formConfig.ltt_single_rep_id,
            transitionTo: FORM_STATES.LTT_EMAIL
          })
        } else if (getters.isLTT) {
          return dispatch('fetchLttConstituencies', {
            postalCode: state.postData.postal_code,
            transitionTo: FORM_STATES.LTT_CHOOSE_REP
          })
        } else {
          // Petition conversion
          dispatch('incrementActionCount')
          dispatch('handleConversionTracking')
          return commit('SET_FORM_STATE', FORM_STATES.THANKS)
        }
      case FORM_STATES.LTT_CHOOSE_REP:
        return commit('SET_FORM_STATE', FORM_STATES.LTT_EMAIL)
      case FORM_STATES.LTT_EMAIL: {
        dispatch('incrementActionCount')
        // LTT conversion
        dispatch('handleConversionTracking')
        return commit('SET_FORM_STATE', FORM_STATES.THANKS)
      }
      case FORM_STATES.FUNDRAISER_INITIAL: {
        if (!getters.paypalEnabled && state.fundraiserUI.selectedProvider === 'paypal') {
          // In the event that paypal is no longer available as a payment option (usually because a
          // currency was chosen that paypal does not support) and paypal was the selected option,
          // we change it to stripe_payment
          commit('UPDATE_FUNDRAISER_UI', {
            selectedProvider: 'stripe',
            selectedProviderMethod: 'stripe_payment'
          })
        }
        return commit('SET_FORM_STATE', FORM_STATES.FUNDRAISER_PERSONAL)
      }
      case FORM_STATES.FUNDRAISER_PERSONAL:
        if (state.fundraiserUI.selectedProvider === 'paypal') {
          // Increment the donation value
          dispatch('incrementActionCount')
          // PayPal payment conversion
          dispatch('handleConversionTracking')
          return commit('SET_FORM_STATE', FORM_STATES.FUNDRAISER_THANKS)
        }
        return commit('SET_FORM_STATE', FORM_STATES.FUNDRAISER_PAYMENT)
      case FORM_STATES.FUNDRAISER_PAYMENT:
        // Increment donation value
        dispatch('incrementActionCount')
        // Stripe payment conversion
        dispatch('handleConversionTracking')
        return commit('SET_FORM_STATE', FORM_STATES.FUNDRAISER_THANKS)
    }
  },
  changeToPreviousPanel({ commit, state, dispatch }) {
    if (
      // eslint-disable-next-line unicorn/prefer-includes
      [FORM_STATES.THANKS, FORM_STATES.LTT_EMAIL, FORM_STATES.FUNDRAISER_PAYMENT].indexOf(
        state.formState
      ) >= 0
    ) {
      dispatch('scrollToTopIfAboveTheFold')
      commit('CLEAR_FORM_GLOBAL_ERRORS')
    }
    switch (state.formState) {
      case FORM_STATES.THANKS:
        if (state.formConfig.type === 'letter_to_target') {
          return commit('SET_FORM_STATE', FORM_STATES.LTT_EMAIL)
        }
        return
      case FORM_STATES.LTT_EMAIL:
        return commit('SET_FORM_STATE', FORM_STATES.LTT_CHOOSE_REP)
      case FORM_STATES.FUNDRAISER_PERSONAL:
        return commit('SET_FORM_STATE', FORM_STATES.FUNDRAISER_INITIAL)
      case FORM_STATES.FUNDRAISER_PAYMENT:
        return commit('SET_FORM_STATE', FORM_STATES.FUNDRAISER_PERSONAL)
    }
  },
  changeToFirstPanel({ commit, getters }) {
    if (getters.isFundraiser) {
      return commit('SET_FORM_STATE', FORM_STATES.FUNDRAISER_INITIAL)
    }
    throw new Error('Unsupported action')
  },
  async fetchLttConstituencies({ state, commit }, { postalCode, transitionTo }) {
    const params = { postal_code: postalCode }

    if (transitionTo) {
      commit('SET_FORM_STATE', transitionTo)
    }

    if (postalCode && postalCode !== state.lttPostalCode) {
      commit('SET_FETCHING', true)
      commit('SET_FORCE_POSTAL_CODE_ERROR', false)
      commit('SET_LTT_POSTAL_CODE', postalCode)

      try {
        const response = await axios.get(
          `${state.baseUrl}/api/campaigns/actions/${state.actionId}/ltt/lookup/`,
          {
            params
          }
        )

        commit('SET_LTT_CONSTITUENCY_DATA', response.data)
      } catch (e) {
        const { status } = e.response || {}
        if (status !== 404) {
          // eslint-disable-next-line no-console
          console.error(e)
        }
        commit('SET_LTT_CONSTITUENCY_DATA', [])
        // Force error state on postcode input if no postcode match
        commit('SET_FORCE_POSTAL_CODE_ERROR', true)
      } finally {
        commit('SET_FETCHING', false)
      }
    }
  },
  async fetchLttEmailInfo({ state, commit }, { repId, transitionTo }) {
    if (repId !== state.repId) {
      commit('SET_FETCHING', true)
      commit('SET_REP_ID', repId)
      try {
        // eslint-disable-next-line camelcase
        const { first_name, last_name } = state.postData || {}
        const response = await axios.post(
          `${state.baseUrl}/api/campaigns/actions/${state.actionId}/ltt/rep/${repId}`,
          {
            first_name,
            last_name
          }
        )
        commit('SET_REP_EMAIL_DATA', response.data)
      } catch (e) {
        const { status } = e.response || {}
        if (status !== 404) {
          commit('SET_REP_EMAIL_DATA', null)
          // eslint-disable-next-line no-console
          console.error(e)
        }
      } finally {
        commit('SET_FETCHING', false)
        if (transitionTo) {
          commit('SET_FORM_STATE', transitionTo)
        }
      }
    }
  },
  resetLttWorkflow({ commit }) {
    commit('SET_FORM_STATE', FORM_STATES.LTT_CHOOSE_REP)
  },
  // If the form top is higher than the viewport top this function scrolls the form top into view
  // the form again
  scrollToTopIfAboveTheFold({ state }) {
    const clientRect = state.getClientRect()
    if (clientRect.top < 0) {
      const vuetify = this.app ? this.app.vuetify : Vuetify
      vuetify.framework.goTo(window.pageYOffset + clientRect.top, {
        duration: 250,
        offset: 20,
        easing: 'easeInOutCubic'
      })
    }
  },
  // Used by the payment form to handle a change in payment type and method
  updatePaymentMethod({ commit, state }, { paymentTypeIndex }) {
    switch (paymentTypeIndex) {
      case 0:
        // Stripe / Card
        return commit('UPDATE_FUNDRAISER_UI', {
          selectedProvider: 'stripe',
          selectedProviderMethod: 'stripe_payment'
        })
      case 1:
        // PayPal / Card
        return commit('UPDATE_FUNDRAISER_UI', {
          selectedProvider: 'paypal',
          selectedProviderMethod: 'paypal_payment'
        })
      default:
        throw new Error('Unhandled paymentTypeIndex')
    }
  },
  updatePaymentFrequency({ commit, state, getters }, { selectedPaymentFrequency }) {
    commit('UPDATE_FUNDRAISER_UI', { selectedPaymentFrequency })

    // If the user has not selected an amount yet, we display the default preselected amount for the
    // currently selected currency
    if (!state.fundraiserUI.amountSelectionIsDirty) {
      commit('UPDATE_FUNDRAISER_UI', {
        amount:
          selectedPaymentFrequency === 'monthly'
            ? getters.monthlyPreselectedAmount
            : getters.oneTimePreselectedAmount
      })
    }
  },
  handlePaymentApiError({ commit }, e) {
    const { status, data } = e.response || {}
    // eslint-disable-next-line no-console
    console.error(e, status)
    if (status === 400) {
      const message = Array.isArray(data) ? data[0] : data
      commit('APPEND_FORM_GLOBAL_ERRORS', { message })
    } else {
      throw e
    }
  },
  // Post to the megaphone endpoint that initialises payments
  async initPayment({ state, getters, commit, dispatch }) {
    const {
      selectedProvider: provider,
      // eslint-disable-next-line camelcase
      selectedProviderMethod: provider_method,
      // eslint-disable-next-line camelcase
      selectedPaymentFrequency: payment_frequency,
      amount,
      currency
    } = state.fundraiserUI

    const payload = {
      provider_product: { provider, payment_frequency },
      provider_method,
      amount,
      currency,
      locale: state.locale,
      ...generateActionData(state, getters)
    }

    // Include the ActionKit ID if it came through the intial page request
    if (state.urlQuery.akid) {
      payload.akid = state.urlQuery.akid
    }

    commit('CLEAR_FORM_GLOBAL_ERRORS')
    commit('SET_FETCHING', true)

    try {
      if (provider === 'stripe' && state.fundraiserUI.stripeClientSecret) {
        // We have a client secret from a previous stripe payment intent initialisation
        // Cancel/delete the previous intent and donation record

        // eslint-disable-next-line camelcase
        const provider_payment_id = state.fundraiserUI.stripeClientSecret
          .split('_')
          .slice(0, 2)
          .join('_')

        // Clear out the old client secret so the UI doesn't render with the old one
        commit('UPDATE_FUNDRAISER_UI', { stripeClientSecret: null })

        axios.post(`${state.baseUrl}/api/payments/actions/${state.actionId}/cancel/`, {
          provider,
          // eslint-disable-next-line camelcase
          provider_payment_id
        })
      }

      const response = await axios.post(
        `${state.baseUrl}/api/payments/actions/${state.actionId}/init/`,
        payload
      )
      commit('UPDATE_FUNDRAISER_UI', {
        eligible_for_premium: response.data.eligible_for_premium,
        premium_campaign: response.data.premium_campaign,
        premium_sizes: response.data.premium_sizes,
        clothing_premium: response.data.clothing_premium,
        donation: response.data.pk
      })
      if (provider === 'stripe') {
        const { client_secret } = response.data
        commit('UPDATE_FUNDRAISER_UI', { stripeClientSecret: client_secret })
      }

      return response
    } catch (e) {
      dispatch('handlePaymentApiError', e)
    } finally {
      commit('SET_FETCHING', false)
    }
  },
  // Initialise the payment process. Expects the API to return a payment id with which to act upon,
  // returned from the correct payment facility
  async commitStripePayment(
    { state, getters, commit, dispatch },
    { stripe, elements, initial = {} }
  ) {
    switch (state.fundraiserUI.selectedProviderMethod) {
      case 'stripe_payment': {
        const payload = {
          elements,
          confirmParams: {
            // TODO: provide build a linkable thanks page and params for tracking
            return_url: 'https://www.google.com',
            payment_method_data: {
              billing_details: {
                name: getters.fullName,
                email: state.formData.email,
                address: {
                  country: state.formData.country
                }
              }
            }
          },
          receipt_email: state.formData.email,
          redirect: 'if_required'
        }
        commit('SET_FETCHING', true)
        commit('CLEAR_FORM_GLOBAL_ERRORS')
        try {
          const result = await stripe.confirmPayment(payload)
          if (result.error) {
            commit('APPEND_FORM_GLOBAL_ERRORS', result.error)
          } else {
            dispatch('handleConversionTracking')
            return (
              (state.fundraiserUI.transation_id = result.paymentIntent.id),
              dispatch('changeToNextPanel')
            )
          }
        } catch (e) {
          dispatch('handlePaymentApiError', e)
        } finally {
          commit('SET_FETCHING', false)
        }
        break
      }
      default:
        throw new Error(`Unhandled payment method "${state.fundraiserUI.selectedProviderMethod}"`)
    }
  },
  async commitPayPalPayment({ dispatch, state }) {
    const response = await dispatch('initPayment')
    // eslint-disable-next-line camelcase
    const { order_id, subscription_id } = response.data
    // eslint-disable-next-line camelcase
    return state.fundraiserUI.selectedPaymentFrequency === 'one_time' ? order_id : subscription_id
  },
  handlePayPalApproved({ dispatch }, orderData) {
    dispatch('changeToNextPanel')
  },
  async fetchPetitionData({ commit, state }, petitions) {
    try {
      const response = await axios.get(`${state.endpoint}?page=${state.page}`)
      commit('SET_PETITION_DATA', response.data)
      console.log(response)
      console.log(petitions)
      return petitions
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  },
  async fetchPopularPetitionData({ commit, state }, popularPetitions) {
    try {
      const response = await axios.get(`${state.endpoint}`)
      commit('SET_POPULAR_PETITION_DATA', response.data)
      return popularPetitions
    } catch (e) {
      console.error(e)
    }
  },
  async fetchPetitionSearchResults({ commit, state }, petitionsSearchResults) {
    try {
      const response = await axios.get(`
      ${state.endpoint}?search=${state.searchTerm}&page=${state.page}`)
      commit('SET_PETITION_SEARCH_RESULTS', response.data)
      return petitionsSearchResults
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}
